// Phase C: the streamed valley, the spike's default scene since the phase was joined (`?scene=world` names it too).
// The world stream's valley from the layout (src/world/scene.ts on @rpg/world), its sim tier (sim.ts: colliders, nav
// tiles in slices and the `world:chunk` event on sim steps) and its view tier (view.ts: meshes under the frame budget),
// the route bot (bots.ts); the places stream's kit, rooms and water (places.ts, places-view.ts), the sky stream's
// clock, weather and look (src/sky/), and the things stream's gathering, breakables and signs (src/things/). The
// Wanderer, the camera, combat and the encounter on the layout; `?seed=N` builds another valley; `?encounter=off` leaves
// the camps out. The handle's keys are under `__rpg.world`; the planted defects join the spike's faults record through
// `worldFaults`; the tours for gates C3 (six place-and-time shots) and C6 (the route on a phone) are in tours.ts.
import type * as THREE from "three";
import type { Invariant } from "@rpg/harness";
import type { LowPolyLook, LowPolySceneLook, ShaftSpot } from "@rpg/look";
import type { Runtime } from "@rpg/runtime";
import { WORLD_FAULTS, cellRng, pointAlong, polyline } from "@rpg/world";
import type { SpikeContent } from "../content";
import type { FaultGroup } from "../faults";
import { createSkyView, type SkyView } from "../sky/view";
import { SKY_ACTIONS } from "../sky/module";
import type { ValleyCamera } from "../valley/camera";
import { worldRouteBot } from "./bots";
import { mountWorldPlacesView, type WorldPlacesView } from "./places-view";
import { worldScene, type WorldAddon, type WorldExtra, type WorldMount } from "./scene";
import type { SceneName } from "../sim/host";
import type { Conditions } from "@rpg/story";
import { WORLD_SIM_FAULTS } from "./sim";
import { mountWorldView, type WorldView } from "./view";
import { worldTours } from "./tours";
import { teleportHero } from "./teleport";

export { worldScene, type WorldAddon, type WorldAddonMount, type WorldAddonParts, type WorldExtra, type WorldMount } from "./scene";

/** The world's planted defects, as `?faults=` and `__rpg.faults()` take them. */
export const WORLD_FAULT_NAMES = ["collidersOneChunkLate", "mathRandomScatter", "navChunkOneSlice"] as const;

/** The group for src/faults.ts's joined record (the switches live beside what they break: the sim tier's in
 *  src/world/sim.ts, the scatter's in @rpg/world). */
export const worldFaults: FaultGroup = {
  names: WORLD_FAULT_NAMES,
  get: (n) => (n === "collidersOneChunkLate" ? WORLD_SIM_FAULTS.collidersOneChunkLate : n === "mathRandomScatter" ? WORLD_FAULTS.mathRandomScatter : n === "navChunkOneSlice" ? WORLD_SIM_FAULTS.navChunkOneSlice : false),
  set: (n, on) => {
    if (n === "collidersOneChunkLate") WORLD_SIM_FAULTS.collidersOneChunkLate = on;
    if (n === "mathRandomScatter") WORLD_FAULTS.mathRandomScatter = on;
    if (n === "navChunkOneSlice") WORLD_SIM_FAULTS.navChunkOneSlice = on;
  },
};

/** Turn on the world's defects `?faults=` names (before the world is built: scatter is drawn as chunks build). */
export function applyWorldFaultSwitches(search = typeof location !== "undefined" ? location.search : ""): void {
  const on = (new URLSearchParams(search).get("faults") ?? "").split(",");
  for (const n of WORLD_FAULT_NAMES) if (on.includes(n)) worldFaults.set(n, true);
}

/** The world scene for the sim mount (the default scene, or `?scene=world`). */
export function worldSceneFor(o: { content: SpikeContent; runtime: Runtime; params: URLSearchParams; difficulty?: string; name?: SceneName; extra?: WorldExtra; addons?: readonly WorldAddon[]; conditions?: Conditions }): WorldMount {
  applyWorldFaultSwitches(`?${o.params.toString()}`);
  const seed = o.params.get("seed");
  return worldScene({ content: o.content, runtime: o.runtime, encounter: o.params.get("encounter") !== "off", ...(o.difficulty ? { difficulty: o.difficulty } : {}), ...(seed !== null && seed !== "" ? { seed: Number(seed) >>> 0 } : {}), ...(o.name ? { name: o.name } : {}), ...(o.extra ? { extra: o.extra } : {}), ...(o.addons ? { addons: o.addons } : {}), ...(o.conditions ? { conditions: o.conditions } : {}) });
}

/** Where the sky's light shafts stand: beside the route through the layout's shaft region, every `spacing` m on
 *  alternating sides, each card's size seeded (the layout's `shafts` block). */
export function shaftSpots(m: WorldMount): ShaftSpot[] {
  const s = m.content.shafts;
  if (!s) return [];
  const route = m.world.layout.paths.find((p) => p.route);
  if (!route) return [];
  const line = polyline(route.points);
  const rng = cellRng(m.world.seed, "shafts");
  const out: ShaftSpot[] = [];
  for (let d = s.spacing / 2; d < line.length && out.length < s.max; d += s.spacing) {
    const [x, z] = pointAlong(line, d);
    const [x2, z2] = pointAlong(line, Math.min(line.length, d + 1));
    if (m.world.terrain.regionAt(x, z) !== s.region) continue;
    const l = Math.hypot(x2 - x, z2 - z) || 1;
    const side = out.length % 2 ? 1 : -1;
    const px = x + (-(z2 - z) / l) * s.side * side;
    const pz = z + ((x2 - x) / l) * s.side * side;
    out.push({ x: px, y: m.world.heightAt(px, pz) - 0.2, z: pz, length: s.length[0] + rng.next() * (s.length[1] - s.length[0]), width: s.width[0] + rng.next() * (s.width[1] - s.width[0]) });
  }
  return out;
}

/** The world's views, handle and invariants, once the sim mount has its look. */
export function worldParts(m: WorldMount, o: { runtime: Runtime; dispatch: (action: string, payload: unknown) => void }) {
  let view: WorldView | null = null;
  let places: WorldPlacesView | null = null;
  let sky: SkyView | null = null;
  // phase E1: an hour the view shows in place of the clock's (photo mode's time of day; the sim's clock is untouched)
  let viewHours: number | null = null;
  let tours: ReturnType<typeof worldTours> | null = null;
  let scene: THREE.Scene | null = null;
  const route = worldRouteBot(m, o.runtime, () => view);
  const chunkOf = new Map(m.world.placements().map((p) => [p.id, p.chunk]));
  const teleport = (x: number, z: number, yaw?: number): void => teleportHero(m, x, z, yaw);
  let frame = 0;
  let liveFrame = -1;
  let live = new Set<string>();
  o.runtime.on("late", () => void frame++, { order: -1e9, name: "world: frame count" });
  const needTours = (): ReturnType<typeof worldTours> => {
    if (!tours) throw new Error("world: no view (the tours need the renderer)");
    return tours;
  };
  const self = {
    /** The sky's view, made before the look (its dome and shaft pass are the look's options). */
    skyView(v: { content: SpikeContent; scene: THREE.Scene; camera: THREE.PerspectiveCamera; renderer: THREE.WebGLRenderer; tier: string }): { view: SkyView; shafts: ShaftSpot[] } {
      sky = createSkyView({
        content: v.content,
        scene: v.scene,
        camera: v.camera,
        renderer: v.renderer,
        tier: v.tier === "low" ? "low" : "high",
        read: () => {
          const l = places?.light() ?? { indoor: null, mix: 0 };
          return { hours: viewHours ?? m.sky().hours(), day: m.sky().state().clock.day, weather: m.sky().blend(), indoor: l.indoor, indoorMix: l.mix };
        },
      });
      return { view: sky, shafts: shaftSpots(m) };
    },
    /** Mount the places' view and the view tier on the valley's look and prime it (inside the look's warm-up, so it
     *  compiles before ready). */
    mountView(v: { look: LowPolyLook; sceneLook: LowPolySceneLook; renderer: THREE.WebGLRenderer; scene: THREE.Scene; camera: THREE.PerspectiveCamera; valleyCamera: ValleyCamera | null; tier: string }): { view: WorldView; clearWarm: () => void } {
      // the test valley's stand-in water (a flat sheet over each water volume's box, the whole brook's 340 m box at
      // its highest surface) gives way to the places' water, fitted to the carved ground per chunk
      scene = v.scene;
      const standIn = v.scene.getObjectByName("valley-water");
      if (standIn) standIn.visible = false;
      places =mountWorldPlacesView({ runtime: o.runtime, m, look: v.look, tier: v.tier === "low" ? "low" : "high", camera: v.valleyCamera, night: () => sky?.outdoor()?.lamps ?? 0 });
      places.prebuild(v.tier === "low" ? "low" : "high");
      const clear = places.warm(v.scene);
      view = mountWorldView({
        world: m.world,
        content: m.content,
        look: v.look,
        sceneLook: v.sceneLook,
        // the sky writes the look every frame: after the overview, its next frame puts the current look back
        ...(sky ? { restoreLook: () => sky!.frame(0, o.runtime.clock.time) } : {}),
        places,
        renderer: v.renderer,
        scene: v.scene,
        camera: v.camera,
        runtime: o.runtime,
        tier: v.tier,
        hero: () => {
          const h = m.rig().hero();
          return { x: h.feet.x, z: h.feet.z, vx: h.vel.x, vz: h.vel.z };
        },
      });
      view.prime();
      tours = worldTours({ m, runtime: o.runtime, renderer: v.renderer, scene: v.scene, look: () => v.look, tier: v.tier === "low" ? "low" : "high", dispatch: o.dispatch, approach: (id, d) => self.handle.approach(id, d), bot: route });
      return { view, clearWarm: clear };
    },
    /** Whether a placement's chunk is live on the view tier (the things' view draws only those). */
    shown: (id: string): boolean => {
      if (!view) return false;
      // the live set read once a frame (the things' view asks for each of its placements)
      if (liveFrame !== frame) {
        liveFrame = frame;
        live = new Set(view.streamer.stats().live as string[]);
      }
      const c = chunkOf.get(id);
      return c !== undefined && live.has(c);
    },
    invariants: (): Invariant[] => [
      () => m.sim().problems().find((p) => !p.includes("out of step")) ?? null,
      () => m.sim().problems().find((p) => p.includes("out of step")) ?? null,
    ],
    handle: {
      /** The world seed and the world hash (terrain, scatter, placements). */
      seed: () => m.world.seed,
      hash: () => m.world.hash(),
      heightAt: (x: number, z: number) => m.world.heightAt(x, z),
      /** The surface tag under (x, z): a floor or a deck there (given a foot's height y, only one at or under it), else the ground's. */
      surfaceAt: (x: number, z: number, y?: number) => m.world.surfaceAt(x, z, y),
      regionAt: (x: number, z: number) => m.world.terrain.regionAt(x, z),
      placements: (kind?: string) => m.world.placements(kind as Parameters<WorldMount["world"]["placements"]>[0]),
      water: () => m.world.water(),
      /** The water surface over (x, z) as the game's collision world answers it (the controller's own query), or null. */
      waterAt: (x: number, z: number): number | null => m.rig().collision.waterSurface(x, z),
      /** The water shapes (fitted to the carved ground): id, shape, box. */
      /** Whether the test valley's stand-in water is drawn (it never should be in the world). */
      standInWater: (): boolean => scene?.getObjectByName("valley-water")?.visible ?? false,
      waterShapes: () => m.water.map((w) => ({ id: w.id, shape: w.shape?.type ?? "box", min: { ...w.min }, max: { ...w.max } })),
      /** The layout's route (its points) and the hero's chunk. */
      route: () => m.world.layout.paths.find((p) => p.route)?.points ?? [],
      /** Resident chunks (the sim tier: colliders, nav tiles) and live ones (the view tier: meshes). */
      chunks: () => {
        const h = m.rig().hero();
        return { hero: m.world.chunkAt(h.feet.x, h.feet.z)?.id ?? null, resident: m.sim().residency.resident(), live: view ? (view.streamer.stats().live as string[]) : [], navTiles: m.nav().tiles().length, navPending: m.sim().slicer.pending().length, navSettled: m.sim().navSettled() };
      },
      /** Both tiers' numbers: the sim tier's checks and residency, the view tier's streamer. */
      stats: () => ({ sim: { ...m.sim().checks(), residency: m.sim().residency.stats() }, view: view?.stats() ?? null, drawn: view?.drawn() ?? null }),
      resetStats: () => {
        m.sim().resetChecks();
        view?.streamer.resetStats();
        view?.resetDrawn();
      },
      problems: () => m.sim().problems(),
      /** The `world:chunk` events emitted since the last reset (loaded, unloading). */
      events: () => m.sim().events(),
      /** The route bot (gate C1): start, run frames, read the report. */
      bot: { start: route.start, run: route.run, report: route.report, stop: route.stop },
      /** Draw the whole layout from above every frame (the overview shot) until called with false. */
      overview: (on = true) => {
        if (!view) throw new Error("world: no view");
        return view.overview(on);
      },
      /** A tool's warp: the hero on the ground at (x, z) facing `yaw`, its chunks loaded now (src/world/teleport.ts). */
      teleport,
      /** The houses' rooms and the interiors director's state; warp the hero `dist` m outside a room's door, facing it
       *  (the sim's camera yaw held). */
      rooms: () => m.rooms.map((r) => ({ id: r.id, piece: r.piece.name, profile: r.spec.profile, light: r.spec.light, bounds: { ...r.spec.bounds } })),
      interiors: () => ({ ...m.places().state(), view: places?.state() ?? null }),
      approach: (id: string, dist = 3) => {
        const room = m.rooms.find((r) => r.id === id);
        if (!room?.piece.interior) throw new Error(`world: no room "${id}"`);
        const i = room.piece.interior;
        const c = Math.cos(room.place.yaw);
        const s = Math.sin(room.place.yaw);
        const dx = room.place.x + i.door.x * c + i.door.z * s;
        const dz = room.place.z - i.door.x * s + i.door.z * c;
        const nx = i.door.nx * c + i.door.nz * s;
        const nz = -i.door.nx * s + i.door.nz * c;
        const x = dx + nx * dist;
        const z = dz + nz * dist;
        const yaw = Math.atan2(-nx, -nz);
        const r = m.rig();
        teleport(x, z, yaw);
        r.yawHeld = true;
        r.camera?.setYaw(yaw);
        r.control().cameraYaw = yaw;
        return { yaw, door: { x: dx, z: dz } };
      },
      /** The sky in the valley: set the hour (forward), force a weather, the day's pace, the ground's wetness (all
       *  through the sim); its state and what the view applied. */
      sky: {
        set: (hours: number) => o.dispatch(SKY_ACTIONS.set, { hours }),
        weather: (id: string, instant = false) => o.dispatch(SKY_ACTIONS.weather, { id, instant }),
        rate: (rate: number) => o.dispatch(SKY_ACTIONS.rate, { rate }),
        wet: (wet: number) => o.dispatch(SKY_ACTIONS.wet, { wet }),
        state: () => {
          const st = m.sky().state();
          return { hours: st.clock.hours, day: st.clock.day, rate: st.clock.rate, period: m.sky().period(), weather: { ...st.weather }, blend: m.sky().blend(), view: sky?.report() ?? null };
        },
        lookAt: (hours: number, weather: string) => sky?.source.at(hours, weather) ?? null,
      },
      /** The shaft cards' spots. */
      shafts: () => shaftSpots(m),
      /** The places' view (kit night, cut-away, room light). */
      places: () => places?.state() ?? null,
      /** Gate C3 in the valley: the six place-and-time shots (frame one: its hour and weather set in the sim, the hero
       *  placed or walked into a room, `ms` of game time drawn), and the perf tour over them. */
      shots: () => needTours().shots(),
      shot: (name: string, ms?: number) => needTours().shot(name, ms),
      lookTour: (ffMs?: number) => needTours().lookTour(ffMs),
      /** Gate C6: the perf tour driving the route bot over the whole route, the sky running. */
      routeTour: (ffMs?: number, rate?: number) => needTours().routeTour(ffMs, rate),
      /** The last tour's report (null before one; status "running", then "done"). */
      perf: () => needTours().perf(),
    },
    state: () => ({ seed: m.world.seed, resident: m.sim().residency.resident().length, live: view ? (view.streamer.stats().live as string[]).length : 0, navPending: m.sim().slicer.pending().length }),
    view: () => view,
    sky: () => sky,
    /** Phase E1: show `hours` on the sky in place of the clock's (null: the clock's again); the sim is untouched. */
    setViewHours: (h: number | null) => void (viewHours = h),
    viewHours: () => viewHours,
  };
  return self;
}
